/**
 * Emotional Themes — twelve cinematic moods that sit on top of the
 * structural reveal style. The theme defines the *feeling* (lighting tint,
 * particle behavior, motion accent), while the style defines the *structure*
 * (backdrop layers, pacing, typography hint).
 *
 * Themes are picked by `pickEmotionalTheme` using emotional signals so two
 * announcements in the same template can land on visibly different moods.
 */

export type EmotionalThemeId =
  | 'golden_memory'
  | 'heavenlight'
  | 'warm_nostalgia'
  | 'celebration_pulse'
  | 'midnight_reflection'
  | 'soul_connection'
  | 'eternal_bond'
  | 'gentle_healing'
  | 'legacy'
  | 'inner_peace'
  | 'through_the_years'
  | 'light_after_darkness';

export type ParticleBehavior = 'drift' | 'rise' | 'sparkle' | 'fall' | 'orbit' | 'still';
export type MotionAccent = 'breath' | 'pulse' | 'sway' | 'drift' | 'still';

export interface EmotionalThemePreset {
  id: EmotionalThemeId;
  label: string;
  /** Color cast applied to the cinematic light layer (HSL hue degrees). */
  hue: number;
  /** 0–1 — saturation of the light cast. */
  saturation: number;
  /** Particle motion behavior. */
  particles: ParticleBehavior;
  /** Subtle ambient motion accent. */
  motion: MotionAccent;
  /** Multiplier applied to the picked style's pacing (1 = unchanged). */
  pacingScale: number;
  /** Held-breath pause before the first intro word, in ms. */
  heldBreathMs: number;
  /** Suggested typography pair. */
  typography: 'serif-classic' | 'serif-editorial' | 'sans-modern' | 'serif-handwritten' | 'mixed-magazine';
}

const PRESETS: Record<EmotionalThemeId, EmotionalThemePreset> = {
  golden_memory:        { id: 'golden_memory',        label: 'Golden Memory',        hue: 42,  saturation: 0.55, particles: 'drift',   motion: 'breath',  pacingScale: 1.10, heldBreathMs: 900,  typography: 'serif-classic' },
  heavenlight:          { id: 'heavenlight',          label: 'Heavenlight',          hue: 208, saturation: 0.30, particles: 'rise',    motion: 'breath',  pacingScale: 1.20, heldBreathMs: 1100, typography: 'serif-editorial' },
  warm_nostalgia:       { id: 'warm_nostalgia',       label: 'Warm Nostalgia',       hue: 28,  saturation: 0.50, particles: 'drift',   motion: 'sway',    pacingScale: 1.15, heldBreathMs: 800,  typography: 'serif-handwritten' },
  celebration_pulse:    { id: 'celebration_pulse',    label: 'Celebration Pulse',    hue: 330, saturation: 0.65, particles: 'sparkle', motion: 'pulse',   pacingScale: 0.85, heldBreathMs: 500,  typography: 'mixed-magazine' },
  midnight_reflection:  { id: 'midnight_reflection',  label: 'Midnight Reflection',  hue: 230, saturation: 0.35, particles: 'still',   motion: 'still',   pacingScale: 1.25, heldBreathMs: 1200, typography: 'serif-classic' },
  soul_connection:      { id: 'soul_connection',      label: 'Soul Connection',     hue: 350, saturation: 0.45, particles: 'orbit',   motion: 'breath',  pacingScale: 1.10, heldBreathMs: 950,  typography: 'serif-editorial' },
  eternal_bond:         { id: 'eternal_bond',         label: 'Eternal Bond',         hue: 18,  saturation: 0.50, particles: 'rise',    motion: 'breath',  pacingScale: 1.15, heldBreathMs: 1000, typography: 'serif-classic' },
  gentle_healing:       { id: 'gentle_healing',       label: 'Gentle Healing',       hue: 150, saturation: 0.30, particles: 'drift',   motion: 'breath',  pacingScale: 1.10, heldBreathMs: 900,  typography: 'sans-modern' },
  legacy:               { id: 'legacy',               label: 'Legacy',               hue: 38,  saturation: 0.40, particles: 'still',   motion: 'still',   pacingScale: 1.20, heldBreathMs: 1100, typography: 'serif-editorial' },
  inner_peace:          { id: 'inner_peace',          label: 'Inner Peace',          hue: 180, saturation: 0.25, particles: 'drift',   motion: 'breath',  pacingScale: 1.15, heldBreathMs: 1000, typography: 'sans-modern' },
  through_the_years:    { id: 'through_the_years',    label: 'Through The Years',    hue: 32,  saturation: 0.50, particles: 'fall',    motion: 'sway',    pacingScale: 1.10, heldBreathMs: 850,  typography: 'mixed-magazine' },
  light_after_darkness: { id: 'light_after_darkness', label: 'Light After Darkness', hue: 50,  saturation: 0.55, particles: 'rise',    motion: 'pulse',   pacingScale: 1.05, heldBreathMs: 950,  typography: 'serif-classic' },
};

export function getEmotionalTheme(id: EmotionalThemeId): EmotionalThemePreset {
  return PRESETS[id];
}

export function allEmotionalThemes(): EmotionalThemePreset[] {
  return Object.values(PRESETS);
}
